import { faGear, faSplit, faTag, faTrashAlt, faXmark } from '@fortawesome/pro-light-svg-icons'
import { Fragment, useCallback, useMemo } from 'react'
import { ITIconToolTip } from '../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/Components/Displays'
import { RowData, TableData } from '../../../../../../../../baseComponents/TwinTable/types'
import twinFetchPost from '../../../../../../../../utils/globals/data'
import useTwinTranslation from '../../../../../../../../utils/hooks/useTwinTranslation'
import { changeErrorMessage } from '../../../../../../../../utils/reducers/reduxDispatch'
import useEditDeleteModal, { SetEditDeleteModalTy } from '../../../../../../../../utils/hooks/useEditDeleteModal'
import ModalDeleteWorkSheetProduct from '../../../../../WorkSheetLayout/ModalsWorkSheetLayout/ModalDeleteWorkSheetProductStatus'
import useWorkSheetProductStatusUrls from '../../../../../../../../utils/hooks/sells/orders/useWorkSheetProductStatusUrls'
import { getAllLabels, getAllWorkSheetGroupStatuses, getUserPermissions } from '../../../../../../../../utils/reducers/getters'
import ModalEditProductStatusView from '../../../../../WorkSheetLayout/WorkSheetProductStatusFlow/WorkSheetProductStatusFlowInner/ModalEditProductStatusView'
import ModalMatchProductWorkSheet from '../../../../../WorkSheetLayout/ModalsWorkSheetLayout/ModalMatchProductWorkSheet'
import ModalWorkSheetSellTag from '../../../../../WorkSheetLayout/ModalsWorkSheetLayout/ModalWorkSheetSellTag'
import PermissionChecker from '../../../../../../../../baseComponents/PermissionChecker'
import { TwinIconToolTip } from '../../../../../../../../baseComponents/TwinIcon'

export interface WorkSheetProductCancelStatusProps extends WorkSheetProductCancelStatusLogicProps { }

export const WorkSheetProductCancelStatus: React.FC<WorkSheetProductCancelStatusProps> = ({ ...params }) => {
    const { t } = useTwinTranslation()
    const { handleCancel } = useWorkSheetProductCancelStatusLogic({ ...params })
    return (
        <ITIconToolTip message={t('cancelLine', 'Cancelar línea')} id={'cancelLine' + params.allRowData.id} onClick={handleCancel} icon={faXmark} />
    )
}
interface WorkSheetProductCancelStatusLogicProps {
    cancelURL: string
    WorkSheetGroupStatusId: number
    allRowData: RowData
    getCompleteData: (inputtable?: TableData) => Promise<void>
}

const useWorkSheetProductCancelStatusLogic = ({ cancelURL, allRowData, WorkSheetGroupStatusId, getCompleteData }: WorkSheetProductCancelStatusLogicProps) => {
    const { t } = useTwinTranslation()
    const errorNotCanceledStatusType = t('notCanceledStatusType', 'No existe un estado de tipo cancelado')
    const handleCancel = useCallback(async () => {
        const response = await twinFetchPost(cancelURL, { id: allRowData.id, WorkSheetId: allRowData.WorkSheetId, WorkSheetGroupStatusId })
        if (response.status === 200) {
            getCompleteData()
            return true
        } else {
            const responseJSON = await response.json()
            const error = responseJSON.errors[0].msg
            if (error === 'notCanceledStatusType') {
                changeErrorMessage(errorNotCanceledStatusType)
            }
        }
    }, [allRowData, cancelURL, WorkSheetGroupStatusId, getCompleteData, errorNotCanceledStatusType])
    return { handleCancel }
}
export interface WorkSheetProductDeleteStatusProps { 
    deleteURL: string
    allRowData: RowData
    getCompleteData: (inputtable?: TableData) => Promise<void>
}

export const WorkSheetProductDeleteStatus: React.FC<WorkSheetProductDeleteStatusProps> = ({ ...params }) => {
    const { t } = useTwinTranslation()
    const { openModal, setOpenModal } = useEditDeleteModal()
    return (
        <Fragment>
            <ITIconToolTip message={t('deleteLine', 'Eliminar línea')} id={'deleteLine' + params.allRowData.id} onClick={() => setOpenModal({ type: 'delete', allRowData: {} })} icon={faTrashAlt} />
            {openModal?.type === 'delete' ? <ModalDeleteWorkSheetProduct {...{ setOpenModal, ...params}} /> : null}
        </Fragment>
    )
}

export interface WorkSheetProductSplitStatusProps {
    allRowData: RowData
    getCompleteData: (inputtable?: TableData) => Promise<void>
    workSheetGroupStatusId: number
}

export const WorkSheetProductSplitStatus: React.FC<WorkSheetProductSplitStatusProps> = ({ getCompleteData, allRowData, workSheetGroupStatusId }) => {
    const { t } = useTwinTranslation()
    const { openModal, setOpenModal } = useEditDeleteModal()
    const workSheetProductStatusURLs = useWorkSheetProductStatusUrls()
    const workSheetProductStatuses = getAllWorkSheetGroupStatuses()
    const memorizedGroup = useMemo(() => workSheetProductStatuses?.dictWorkSheetGroupStatus?.[String(workSheetGroupStatusId)] || {} || {}, [workSheetProductStatuses, workSheetGroupStatusId])
    return (
        <Fragment>
            <ITIconToolTip message={t('dividedProductStatus', 'Dividir el estado del producto')} id={'dividedProductStatus' + allRowData.id} onClick={() => setOpenModal({ type: 'create', allRowData })} icon={faSplit} />
            {openModal?.type === 'create' ? <ModalEditProductStatusView setOpenModal={setOpenModal} onSubmit={() => { }} allRowData={openModal?.allRowData} setOpened={() => setOpenModal(null)} memorizedGroup={memorizedGroup} workSheetProductStatusURLs={workSheetProductStatusURLs} getProducts={getCompleteData} /> : null}
            {openModal?.type === 'delete' ? <ModalWorkSheetSellTag allRowData={openModal.allRowData} setOpenModal={setOpenModal} onSubmit={() => getCompleteData()} /> : null}
            {openModal?.type === 'edit' ? <ModalMatchProductWorkSheet groupProductByStatusURL={'/api/app/order/sell/workSheet/workSheetProduct/groupWorkSheetProduct'} allRowData={openModal.allRowData} setOpenModal={setOpenModal} getCompleteData={getCompleteData} /> : null}
        </Fragment>
    )
}


interface PrintWorkSheetTagsButtonProps {
    id: string
    allRowData: RowData
    className?: string
    setOpenModal: SetEditDeleteModalTy
}

export const PrintWorkSheetTagsButton: React.FC<PrintWorkSheetTagsButtonProps> = ({ id, className, allRowData, setOpenModal }) => {
    const { t } = useTwinTranslation()
    const labels = getAllLabels()
    if (!labels || !Object.keys(labels).length) {
        return null
    }
    return (
        <PermissionChecker userPermissions={getUserPermissions('config.label')} permission={'read'} >
            <div className={'flex relative items-center mt-4 ' + (className || '')}>
                <TwinIconToolTip id={id + 'tag'} classNameIcon={'regular22 hover:text-green-43 row_icon' + (className || '')} message={t('printTags', 'Imprimir etiquetas')} icon={faTag} onClick={() => setOpenModal({ type: 'edit', allRowData: { id: allRowData.WorkSheetId, WorkSheetProductId: id, LabelId: allRowData['WorkSheetProductStatus.LabelId'], SpecificPriceRuleId: allRowData['WorkSheetProductStatus.SpecificPriceRuleForLabelId'], tagQuantity: allRowData?.quantity, subType: 'printTags' }})} />
            </div>
        </PermissionChecker>
    )
}
interface ChangeQuantityButtonProps {
    id: string
    allRowData: RowData
    className?: string
    setOpenModal: SetEditDeleteModalTy
}

export const ChangeQuantityButton: React.FC<ChangeQuantityButtonProps> = ({ id, className, allRowData, setOpenModal }) => {
    const { t } = useTwinTranslation()
    if (allRowData?.['UniquePKSellProduct.someProcessed'] || (!allRowData?.['UniquePKSellProduct.someProcessed'] && !allRowData?.['UniquePKSellProduct.SellOrderProductId'])) {
        return null
    }
    return (
        <PermissionChecker userPermissions={getUserPermissions('sellOther.updateSellQuantity')} permission={'read'} >
            <TwinIconToolTip id={id + 'quantity'} classNameIcon={'regular22 hover:text-green-43 row_icon ml-5 mb-1 ' + (className || '')} message={t('changeQuantity', 'Cambiar cantidad')} icon={faGear} onClick={() => setOpenModal({ type: 'edit', allRowData: { id: allRowData.id, quantity: allRowData.quantity, desiredQuantity: allRowData.desiredQuantity, SellOrderProductId: allRowData?.['UniquePKSellProduct.SellOrderProductId'], subType: 'changeQuantity'}})} />
        </PermissionChecker>
    )
}